import { DepositsItem } from "@/modules/user/types/wallet";
import { formatCalculatedDepositRewards } from "./deposit-rewards-calculator";
import "../../auth/utils/arrays";
import { HistoryRecord } from "@/modules/user/types/history";
import { constants } from "../consts";

const oneDayMs = 24 * 60 * 60 * 1000;

export const calculateDailyReward = (
  deposits: DepositsItem[],
  targetDate: Date
) => {
  const depositsWithCurrentRewards = deposits.map((dep) =>
    formatCalculatedDepositRewards(
      dep.deposit.tokenAmount,
      dep.deposit.withdrawnTokenAmount,
      new Date(targetDate.getTime() - oneDayMs),
      new Date(dep.deposit.createdAt)
    )
  );

  const depositsWithNextDayRewards = deposits.map((dep) =>
    formatCalculatedDepositRewards(
      dep.deposit.tokenAmount,
      dep.deposit.withdrawnTokenAmount,
      targetDate,
      new Date(dep.deposit.createdAt)
    )
  );

  const currentRewards = depositsWithCurrentRewards.sum(
    (d) => d.targetDateAvailable.amount,
    0
  );

  const nextDayRewards = depositsWithNextDayRewards.sum(
    (d) => d.targetDateAvailable.amount,
    0
  );

  return parseFloat(
    Math.max(nextDayRewards - currentRewards, 0).toFixed(
      constants.TOKEN_DECIMALS
    )
  );
};

export const getDatesFromCreatedAtToToday = (
  deposits: DepositsItem[]
): Date[] => {
  if (deposits.length === 0) return [];

  const earliestCreatedAt = deposits
    .map((d) => new Date(d.deposit.createdAt))
    .reduce((min, date) => (date < min ? date : min));

  earliestCreatedAt.setUTCHours(0, 0, 0, 0);

  const now = new Date();
  now.setUTCHours(0, 0, 0, 0);

  const totalDays =
    Math.floor((now.getTime() - earliestCreatedAt.getTime()) / oneDayMs) + 1;

  const dates: Date[] = [];
  // From next day after createdAt to yesterday
  for (let i = totalDays - 2; i >= 1; i--) {
    const date = new Date(earliestCreatedAt.getTime() + i * oneDayMs);
    dates.push(date);
  }

  return dates;
};

export const calculateDailyRewardsForAllDeposits = (
  deposits: DepositsItem[]
) => {
  const dates = getDatesFromCreatedAtToToday(deposits);

  const result: HistoryRecord[] = [];

  for (let i = 0; i < dates.length; i++) {
    const date = dates[i];

    const reward = calculateDailyReward(deposits, date);

    result.push({
      type: "dep-reward-daily",
      createdAt: date.toISOString(),
      details: {
        tokenAmount: reward,
      },
    });
  }

  return result;
};

type RewardTier = {
  from: number;
  to: number;
  percent: number;
};

const rewardTiers: RewardTier[] = [
  { from: 1, to: 7, percent: 0.5 },
  { from: 8, to: 14, percent: 1 },
  { from: 15, to: 21, percent: 1.5 },
  { from: 22, to: 28, percent: 2 },
  { from: 29, to: 99, percent: 2.5 },
  { from: 100, to: 135, percent: 3 },
];

export const calculateDailyDepositReward = (
  amount: number,
  createdAt: Date | string,
  targetDate: Date | string
): number => {
  const start = new Date(createdAt);
  const end = new Date(targetDate);

  start.setUTCHours(0, 0, 0, 0);
  end.setUTCHours(0, 0, 0, 0);

  const diffDays = Math.floor(
    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (diffDays < 1) return 0;

  const tier = rewardTiers.find((t) => diffDays >= t.from && diffDays <= t.to);
  const percent = tier ? tier.percent : 0;

  const factor = 10 ** constants.TOKEN_DECIMALS;

  return Math.floor((amount * percent / 100) * factor) / factor;
};

export const calculateDailyDepositsReward = (
  deposits: DepositsItem[],
  targetDate: Date | string
): number => {
  const res = [];

  for (let i = 0; i < deposits.length; i++) {
    const { deposit } = deposits[i];

    const dailyReward = calculateDailyDepositReward(
      deposit.tokenAmount,
      deposit.createdAt,
      targetDate
    );

    res.push(dailyReward);
  }

  return res.reduce((acc, cur) => acc + cur, 0);
};

export const calculateDailyDepositsRewardToCurrent = (
  deposits: DepositsItem[]
): HistoryRecord[] => {
  const dates = getDatesFromCreatedAtToToday(deposits);

  const result: HistoryRecord[] = [];

  for (let i = 0; i < dates.length; i++) {
    const date = dates[i];

    const reward = calculateDailyDepositsReward(deposits, date);

    result.push({
      type: "dep-reward-daily",
      createdAt: date.toISOString(),
      details: {
        tokenAmount: reward,
      },
    });
  }

  return result;
};
